import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Info } from 'lucide-react';

/**
 * Table column header with an info icon; hover shows a short definition (portaled, not clipped).
 */
export function TableHeaderInfo({ label, tip, className = '', thProps = {} }) {
  const anchorRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });

  const updatePosition = useCallback(() => {
    const el = anchorRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setCoords({
      top: rect.bottom + 8,
      left: rect.left + rect.width / 2,
    });
  }, []);

  const show = useCallback(() => {
    updatePosition();
    setVisible(true);
  }, [updatePosition]);

  const hide = useCallback(() => setVisible(false), []);

  useEffect(() => {
    if (!visible) return undefined;
    const onScrollOrResize = () => updatePosition();
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [visible, updatePosition]);

  return (
    <th className={`relative ${className}`} {...thProps}>
      <span className="inline-flex items-center gap-1">
        <span>{label}</span>
        <button
          ref={anchorRef}
          type="button"
          className="inline-flex shrink-0 p-0.5 rounded text-gray-400 dark:text-gray-500 hover:text-[#03396c] dark:hover:text-blue-400 focus:outline-none focus-visible:ring-1 focus-visible:ring-[#03396c]"
          aria-label={`About ${label}: ${tip}`}
          onMouseEnter={show}
          onMouseLeave={hide}
          onFocus={show}
          onBlur={hide}
        >
          <Info className="h-3 w-3" aria-hidden />
        </button>
      </span>
      {visible &&
        createPortal(
          <div
            role="tooltip"
            style={{
              position: 'fixed',
              top: coords.top,
              left: coords.left,
              transform: 'translateX(-50%)',
              zIndex: 9999,
            }}
            className="pointer-events-none w-max max-w-[260px] px-2.5 py-1.5 rounded-md text-[10px] font-normal normal-case tracking-normal leading-snug text-white bg-gray-900 dark:bg-gray-700 shadow-lg"
          >
            {tip}
          </div>,
          document.body,
        )}
    </th>
  );
}

export default TableHeaderInfo;
